import { UserInfoDTO } from "src/_genApi/scs/models/UserInfoDTO";
import { getDeviceId } from '../device-id';

/** Shared localStorage keys — must match Studio (@story-craft/shared/auth) */
export const AUTH_TOKEN_KEY = "storycraft_auth_token";
export const AUTH_USER_KEY = "storycraft_auth_user";

export interface LoginParams {
  accessToken: string;
  deviceId: string;
  socialType: string;
}

export interface LoginResponse {
  user: UserInfoDTO;
  token: string;
}

/**
 * Two-step login:
 * 1. POST /api/get-zps-token — exchange access token for JWT
 * 2. GET /api/me — fetch user info with the JWT
 */
export async function login(baseUrl: string, params: LoginParams): Promise<LoginResponse> {
  const response = await fetch(`${baseUrl}/api/get-zps-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      accessToken: params.accessToken,
      deviceId: params.deviceId,
      socialType: params.socialType,
    }),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(
      `[auth] /api/get-zps-token ${response.status} ${response.statusText}` +
      (body ? ` — ${body.slice(0, 200)}` : '')
    );
  }

  const { token } = (await response.json()) as { token: string };

  const user = await getMe(baseUrl, token);
  if (!user) {
    throw new Error('[auth] login succeeded but /api/me returned no user');
  }

  return { user, token };
}

/** POST /api/me — fetch current user info, reporting device ID. Returns null on failure. */
export async function getMe(baseUrl: string, token: string): Promise<UserInfoDTO | null> {
  let response: Response;
  try {
    response = await fetch(`${baseUrl}/api/me`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ deviceId: getDeviceId() }),
    });
  } catch (e) {
    console.error('[auth] /api/me network error', e);
    return null;
  }

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    console.warn(`[auth] /api/me ${response.status}`, body.slice(0, 200));
    return null;
  }

  return (await response.json()) as UserInfoDTO;
}
